import { useState } from "react";
import msg from "components/string-table";
import { Accordion, Colors, Icon } from "main";
import { generateSwingSupportURL } from "swing-utils";

import { ValuesOf } from "~utils";

import styles from "./SwingHero.module.css";

export type SwingHeroProps = {
  subDaysWorked: number;
  score?: number;
  isSwingHero: boolean;
  isSwingHeroEligible?: boolean;
  isHourly: boolean;
  onClick?: {
    requestsOnClick: () => void;
    skillBuilderOnClick: () => void;
  };
};

/**
 * SwingHero
 *
 * Displays a user's Swing Hero status with appropriate messaging
 * and accordion content based on their eligibility and current hero status.
 */
export function SwingHero(props: SwingHeroProps): JSX.Element {
  const { subDaysWorked, score, isSwingHero, isSwingHeroEligible, isHourly } = props;

  /*** Status ***/
  const status = getHeroStatus({
    isSwingHero,
    isSwingHeroEligible,
    subDaysWorked,
    score,
  });
  const { subtext, statusText, statusIcon } = getStatusDetails(status);

  /*** Accordion ***/
  const accordion = getAccordion(status, isHourly);
  const [isAccordionOpen, setIsAccordionOpen] = useState(true);
  function handleAccordionToggle() {
    setIsAccordionOpen(!isAccordionOpen);
  }

  return (
    <div className={styles.container}>
      {/* Status Section */}
      <div className={styles.statusSection}>
        <p className={styles.statusTitle}>{msg("SWING_HERO_YOUR_STATUS")}</p>
        <div className={styles.statusContainer}>
          {statusIcon}
          <p className={styles.status}>{statusText}</p>
        </div>
        {subtext && <p className={styles.subtext}>{subtext}</p>}
      </div>

      {/* Accordion Section */}
      <Accordion
        title={accordion.title}
        isOpen={isAccordionOpen}
        onToggle={handleAccordionToggle}
        style={{ borderBottom: "0px", width: "100%", padding: "0px" }}
        headerStyle={{ padding: "0px" }}
        titleStyle={{ fontSize: 14, fontWeight: 600, lineHeight: "24px" }}
      >
        <div className={styles.accordionContent}>{accordion.content}</div>
      </Accordion>
    </div>
  );
}

/**
 * Determines the user's Swing Hero status based on their eligibility and current state
 */

type SwingHeroStatus = "hero" | "eligible" | "ineligible";

function getHeroStatus(params: {
  isSwingHero: boolean;
  isSwingHeroEligible?: boolean;
  subDaysWorked: number;
  score?: number;
}): SwingHeroStatus {
  const { isSwingHero, isSwingHeroEligible } = params;

  if (isSwingHeroEligible === false) {
    return "ineligible";
  }
  if (isSwingHero) {
    return "hero";
  }
  return "eligible";
}

function getStatusDetails(status: SwingHeroStatus) {
  switch (status) {
    case "hero":
      return {
        statusText: `${msg("SUB_PROFILE_CONTACT_SWING")} ${msg("SUB_PROFILE_CONTACT_HERO")}`,
        subtext: "",
        statusIcon: <Icon name="Swing Hero" />,
      };
    case "eligible":
      return {
        statusText: msg("SWING_HERO_SWING_SUB"),
        subtext: msg("SWING_HERO_ELIGIBLE_SUBTEXT"),
        statusIcon: <Icon name="Star Unfilled" color={Colors.blue500} />,
      };
    case "ineligible":
      return {
        statusText: msg("SWING_HERO_SWING_SUB"),
        subtext: msg("SWING_HERO_INELIGIBLE_SUBTEXT"),
        statusIcon: null,
      };
    default:
      return {
        statusText: msg("SWING_HERO_SWING_SUB"),
        subText: "",
        statusIcon: null,
      };
  }
}

type AccordionItem =
  | { iconName: string; text: string; iconColor?: ValuesOf<typeof Colors> }
  | { listDescription: string };

function getAccordion(status: SwingHeroStatus, isHourly: boolean) {
  const skillBuilderUrl = msg("SWING_HERO_SKILL_BUILDER_URL");
  const heroesUrl = generateSwingSupportURL(
    isHourly
      ? msg("SWING_HERO_SUPPORT_ARTICLE_LINK_W2")
      : msg("SWING_HERO_SUPPORT_ARTICLE_LINK_1099"),
  );
  const supportUrl = generateSwingSupportURL("/hc/en-us/requests/new");

  switch (status) {
    case "hero":
      return {
        title: msg("SWING_HERO_MAINTAIN_STATUS_TITLE"),
        content: getAccordionContent(
          [
            { iconName: "Check Circle", text: msg("SWING_HERO_MAINTAIN_PROFESSIONAL_BEHAVIOR") },
            {
              iconName: "Check Circle",
              text: msg("SWING_HERO_IMPROVE_SKILLS_WITH_SKILL_BUILDER", { skillBuilderUrl }),
            },
            { listDescription: msg("SWING_HERO_QUALIFYING_FOR_DESCRIPTION") },
            { iconName: "Star Circle", text: msg("SWING_HERO_PRIORITY_ACCESS_PERK") },
            { iconName: "Star Circle", text: msg("SWING_HERO_INCREASED_EARNINGS_PERK") },
          ],
          [msg("SWING_HERO_LEARN_MORE_ABOUT_HEROES", { heroesUrl })],
        ),
      };
    case "eligible":
      return {
        title: msg("SWING_HERO_HOW_TO_BECOME_TITLE"),
        content: getAccordionContent(
          [
            { iconName: "Check Circle", text: msg("SWING_HERO_MAINTAIN_PROFESSIONAL_BEHAVIOR") },
            {
              iconName: "Check Circle",
              text: msg("SWING_HERO_IMPROVE_SKILLS_WITH_SKILL_BUILDER", { skillBuilderUrl }),
            },
            { listDescription: msg("SWING_HERO_TO_QUALIFY_FOR_DESCRIPTION") },
            { iconName: "Star Circle", text: msg("SWING_HERO_PRIORITY_ACCESS_PERK") },
            { iconName: "Star Circle", text: msg("SWING_HERO_INCREASED_EARNINGS_PERK") },
          ],
          [msg("SWING_HERO_LEARN_MORE_ABOUT_HEROES", { heroesUrl })],
        ),
      };
    case "ineligible":
      return {
        title: msg("SWING_HERO_TIPS_TITLE"),
        content: getAccordionContent(
          [
            {
              iconName: "Check Circle",
              iconColor: Colors.black,
              text: msg("SWING_HERO_INELIGIBLE_LIST_FIRST_ITEM"),
            },
            {
              iconName: "Check Circle",
              iconColor: Colors.black,
              text: msg("SWING_HERO_INELIGIBLE_LIST_SECOND_ITEM"),
            },
            {
              iconName: "Check Circle",
              iconColor: Colors.black,
              text: msg("SWING_HERO_IMPROVE_SKILLS_WITH_SKILL_BUILDER", { skillBuilderUrl }),
            },
          ],
          [
            msg("SWING_HERO_CONTACT_SUPPORT_QUESTIONS", { supportUrl }),
            msg("SWING_HERO_LEARN_MORE_ABOUT_HEROES", { heroesUrl }),
          ],
        ),
      };
    default:
      return {
        title: msg("SWING_HERO_HOW_TO_BECOME_TITLE"),
        content: getAccordionContent([], [""]),
      };
  }
}

function getAccordionContent(items: Array<AccordionItem>, footers: Array<string>) {
  return (
    <div>
      <ul className={styles.accList}>
        {items.map((item, index) => {
          if ("listDescription" in item) {
            return (
              <div key={index} className={styles.accListDescription}>
                <p>{item.listDescription}</p>
              </div>
            );
          } else {
            return (
              <li key={index} className={styles.accListItem}>
                <div className={styles.accListItemIcon}>
                  <Icon name={item.iconName as any} color={item.iconColor || Colors.green500} />
                </div>
                <span dangerouslySetInnerHTML={{ __html: item.text }} />
              </li>
            );
          }
        })}
      </ul>
      {footers.map((text, index) => (
        <div key={index} className={styles.accListFooter}>
          <p dangerouslySetInnerHTML={{ __html: text }} />
        </div>
      ))}
    </div>
  );
}
